import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Menu, Sun, Moon, Monitor, ChevronLeft, Bell, X } from 'lucide-react';
import { useTheme } from '../../hooks/useTheme';
import { Spinner } from '../ui';
import projectService from '../../services/projectService';

const isAdminOrPm = (u) =>
  u?.role === 'admin' || u?.role === 'pm' ||
  u?.app_metadata?.role === 'admin' || u?.app_metadata?.role === 'pm';

const canSeeNotificationsRole = (u) =>
  isAdminOrPm(u) || u?.role === 'reviewer' || u?.app_metadata?.role === 'reviewer';

const Header = ({ 
  user, 
  showUserDropdown, 
  setShowUserDropdown, 
  setSidebarOpen, 
  logout, 
  logoutLoading 
}) => {
  const navigate = useNavigate();
  const [theme, setTheme] = useTheme();
  const [showThemeSubmenu, setShowThemeSubmenu] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [notificationsLoading, setNotificationsLoading] = useState(false);

  const canSeeNotifications = canSeeNotificationsRole(user);

  const fetchUnreadCount = useCallback(async () => {
    if (!canSeeNotifications) return;
    const count = await projectService.getUnreadNotificationsCount();
    setUnreadCount(count);
  }, [canSeeNotifications]);

  useEffect(() => {
    fetchUnreadCount();
    const t = setInterval(fetchUnreadCount, 60000);
    return () => clearInterval(t);
  }, [fetchUnreadCount]);

  const openNotifications = async () => {
    if (!canSeeNotifications) return;
    setShowNotifications((prev) => {
      if (!prev) {
        setNotificationsLoading(true);
        // Fetch full history (read + unread) so notifications stay visible
        projectService.getNotifications(false, 50).then((list) => {
          setNotifications(list);
          setNotificationsLoading(false);
        });
      }
      return !prev;
    });
  };

  const handleNotificationClick = async (n) => {
    const id = n.id;
    const projectId = n.project_id;
    if (!n.is_read) {
      await projectService.markNotificationRead(id);
      setNotifications((prev) => prev.map((item) => (item.id === id ? { ...item, is_read: true } : item)));
      setUnreadCount((c) => Math.max(0, c - 1));
    }
    setShowNotifications(false);
    if (projectId) {
      const type = (n.type || '').toLowerCase();
      if (type.includes('keyword')) {
        navigate(`/keyword/projects/${projectId}`);
      } else if (type.includes('synopsis')) {
        navigate(`/synopsis/projects/${projectId}`);
      } else {
        navigate(`/keyword/projects/${projectId}`);
      }
    }
  };

  const handleMarkAllAsRead = async () => {
    const unread = notifications.filter((n) => !n.is_read);
    if (unread.length === 0) return;
    try {
      await Promise.all(unread.map((n) => projectService.markNotificationRead(n.id)));
      setNotifications((prev) => prev.map((item) => ({ ...item, is_read: true })));
      setUnreadCount(0);
    } catch (err) { /* ignore */ }
  };

  const handleDeleteNotification = async (e, n) => {
    e.stopPropagation();
    try {
      await projectService.deleteNotification(n.id);
      setNotifications((prev) => prev.filter((item) => item.id !== n.id));
      if (!n.is_read) setUnreadCount((c) => Math.max(0, c - 1));
    } catch (err) { /* ignore */ }
  };

  const themes = [
    { value: 'auto', label: 'Auto', icon: Monitor },
    { value: 'light', label: 'Light', icon: Sun },
    { value: 'dark', label: 'Dark', icon: Moon }
  ];
  
  const currentThemeIcon = themes.find(t => t.value === theme)?.icon || Monitor;
  return (
    <nav className="border-border bg-background fixed top-0 left-0 right-0 z-50 flex h-11 items-center justify-between border-b px-4">
      <div className="flex w-full justify-between items-center">
        <div className="flex items-center gap-2">
          <button
            onClick={() => setSidebarOpen(prev => !prev)}
            className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-neutral-900 transition-colors md:hidden"
            aria-label="Toggle sidebar"
          >
            <Menu className="w-4 h-4 text-gray-600 dark:text-neutral-400" />
          </button>
          <Link to="/dashboard" className="text-foreground text-xl font-bold tracking-wide hover:opacity-60">
            yirum
          </Link>
        </div>
        
        <div className="flex items-center space-x-2">
          {canSeeNotifications && (
            <div className="relative">
              <button
                onClick={openNotifications}
                className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-neutral-900 transition-colors text-gray-600 dark:text-neutral-400 relative"
                aria-label="Notifications"
              >
                <Bell className="w-4 h-4" />
                {unreadCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 min-w-[1rem] h-4 px-1 flex items-center justify-center bg-red-500 text-white text-xs font-medium rounded-full">
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                )}
              </button>
              {showNotifications && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setShowNotifications(false)} aria-hidden="true" />
                  <div className="absolute right-0 mt-2 w-80 max-h-96 overflow-auto bg-white dark:bg-neutral-900 border border-gray-200 dark:border-neutral-700 rounded-lg shadow-lg z-50">
                    <div className="flex items-center justify-between gap-2 p-2 border-b border-gray-200 dark:border-neutral-700">
                      <span className="font-medium text-sm text-gray-900 dark:text-neutral-100">Notification history</span>
                      {unreadCount > 0 && (
                        <button
                          type="button"
                          onClick={handleMarkAllAsRead}
                          className="text-xs font-medium text-primary hover:text-primary/80 hover:underline shrink-0"
                        >
                          Mark as read
                        </button>
                      )}
                    </div>
                    {notificationsLoading ? (
                      <div className="p-4 flex justify-center"><Spinner size="sm" /></div>
                    ) : notifications.length === 0 ? (
                      <div className="p-4 text-sm text-gray-500 dark:text-neutral-400">No notifications yet</div>
                    ) : (
                      <ul className="py-1">
                        {notifications.map((n) => (
                          <li key={n.id} className="border-b border-gray-100 dark:border-neutral-800 last:border-0 hover:bg-gray-50 dark:hover:bg-neutral-800 group flex">
                            <button
                              type="button"
                              onClick={() => handleNotificationClick(n)}
                              className={`flex-1 min-w-0 text-left px-3 py-2 text-sm ${
                                n.is_read
                                  ? 'text-gray-500 dark:text-neutral-400'
                                  : 'text-gray-900 dark:text-neutral-100 font-medium'
                              }`}
                            >
                              <div className="flex items-center gap-2">
                                <span>{n.title}</span>
                                {!n.is_read && (
                                  <span className="w-2 h-2 rounded-full bg-blue-500 flex-shrink-0" aria-hidden />
                                )}
                              </div>
                              {n.message && <div className="text-xs text-gray-500 dark:text-neutral-400 truncate mt-0.5">{n.message}</div>}
                              {n.created_at && <div className="text-xs text-gray-400 dark:text-neutral-500 mt-0.5">{new Date(n.created_at).toLocaleString()}</div>}
                              {n.project_id && <div className="text-xs text-gray-400 dark:text-neutral-500">Click to open project</div>}
                            </button>
                            <button
                              type="button"
                              onClick={(e) => handleDeleteNotification(e, n)}
                              className="p-2 shrink-0 text-gray-400 hover:text-red-600 dark:hover:text-red-400 opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity"
                              aria-label="Delete notification"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </>
              )}
            </div>
          )}
          <div className="relative user-dropdown">
            <button
              onClick={() => setShowUserDropdown(!showUserDropdown)}
              className="flex items-center space-x-1.5 text-sm text-gray-600 dark:text-neutral-400 hover:text-gray-900 dark:hover:text-gray-100 transition-colors"
            >
              <div className="w-6 h-6 rounded-full flex items-center justify-center overflow-hidden bg-gray-200 dark:bg-gray-700 shrink-0">
                {user?.picture ? (
                  <img src={user.picture} alt="" className="w-full h-full object-cover" referrerPolicy="no-referrer" />
                ) : (
                  <span className="text-xs font-medium text-gray-700 dark:text-neutral-300">
                    {user?.name?.charAt(0) || user?.email?.charAt(0) || 'U'}
                  </span>
                )}
              </div>
              <svg 
                className={`w-3.5 h-3.5 transition-transform ${showUserDropdown ? 'rotate-180' : ''}`} 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            
            {/* User Dropdown */}
            {showUserDropdown && (
              <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-neutral-900 border border-gray-200 dark:border-neutral-700 rounded-lg shadow-lg z-50">
                <div className="py-1">
                  <div className="px-3 py-1.5 border-b border-gray-100 dark:border-neutral-700 flex items-center gap-2">
                    <div className="w-7 h-7 rounded-full flex items-center justify-center overflow-hidden bg-gray-200 dark:bg-gray-700 shrink-0">
                      {user?.picture ? (
                        <img src={user.picture} alt="" className="w-full h-full object-cover" referrerPolicy="no-referrer" />
                      ) : (
                        <span className="text-xs font-medium text-gray-700 dark:text-neutral-300">
                          {user?.name?.charAt(0) || user?.email?.charAt(0) || 'U'}
                        </span>
                      )}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-900 dark:text-neutral-100">{user?.name || user?.email || 'User'}</p>
                      <p className="text-xs text-gray-500 dark:text-neutral-400">Logged in as {user?.role ? String(user.role) : 'User'}</p>
                    </div>
                  </div>
                  
                  {/* Theme Menu Item */}
                  <div 
                    className="relative"
                    onMouseEnter={() => setShowThemeSubmenu(true)}
                    onMouseLeave={() => setShowThemeSubmenu(false)}
                  >
                    <button
                      className="w-full text-left px-3 py-1.5 text-sm text-gray-700 dark:text-neutral-300 hover:bg-gray-100 dark:hover:bg-neutral-800 transition-colors flex items-center justify-between"
                    >
                      <div className="flex items-center">
                        <ChevronLeft className="w-4 h-4 mr-2" />
                        Themes
                      </div>
                      {React.createElement(currentThemeIcon, { className: "w-4 h-4" })}
                    </button>
                    
                    {/* Theme Submenu - flush/overlap so mouse can move from trigger to submenu without closing */}
                    {showThemeSubmenu && (
                      <div className="absolute right-full top-0 -mr-px w-32 bg-white dark:bg-neutral-900 border border-gray-200 dark:border-neutral-700 rounded-lg shadow-lg">
                        <div className="py-1">
                          {themes.map(({ value, label, icon: Icon }) => (
                            <button
                              key={value}
                              onClick={() => {
                                setTheme(value);
                                setShowUserDropdown(false);
                                setShowThemeSubmenu(false);
                              }}
                              className={`w-full text-left px-3 py-1.5 text-sm transition-colors flex items-center ${
                                theme === value
                                  ? 'bg-sky-50 dark:bg-sky-900/30 text-sky-600 dark:text-sky-400'
                                  : 'text-gray-700 dark:text-neutral-300 hover:bg-gray-100 dark:hover:bg-neutral-800'
                              }`}
                            >
                              <Icon className="w-4 h-4 mr-2" />
                              {label}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                  
                  <button
                    onClick={logout}
                    disabled={logoutLoading}
                    className="w-full text-left px-3 py-1.5 text-sm text-gray-700 dark:text-neutral-300 hover:bg-gray-100 dark:hover:bg-neutral-800 transition-colors disabled:text-gray-400 disabled:cursor-not-allowed flex items-center"
                  >
                    {logoutLoading ? (
                      <>
                        <Spinner size="sm" className="mr-2 text-muted-foreground" />
                        Logging out...
                      </>
                    ) : (
                      <>
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                        </svg>
                        Logout
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Header;

